// =============================================================================
// useRemoteMedia
// =============================================================================
// Binds a single remote MediaStream to a single media element (audio or video).
//
// One hook instance owns one output path for one stream. For a 1-on-1 call that
// is a single sink; for an N-participant grid (W2) the consumer renders one
// element per remote stream and calls this hook once per element, so each stream
// gets exactly one sink and there is no double-audio. Do not feed it the whole
// remoteStreams map - feed it one stream per element.

import { useCallback } from 'react';

interface UseRemoteMediaOptions {
  // Audio output device id. When supplied (and supported) the element is routed
  // to that device via setSinkId. The speaker-routing UI that sets this lands in
  // W2-CALLS; the parameter is the seam so that wiring needs no rewrite here.
  sinkId?: string;
}

type SinkableMediaElement = HTMLMediaElement & {
  setSinkId?: (deviceId: string) => Promise<void>;
};

/**
 * Returns a ref callback to attach to an <audio> or <video> element. The element
 * is bound to `stream` whenever either the stream or the element changes.
 */
export function useRemoteMedia(
  stream: MediaStream | null | undefined,
  options: UseRemoteMediaOptions = {}
): (element: HTMLMediaElement | null) => void {
  const { sinkId } = options;

  return useCallback(
    (element: HTMLMediaElement | null) => {
      if (!element) {
        return;
      }

      // Only reassign when the stream actually differs, so an unrelated re-render
      // (e.g. a new Map identity holding the same stream) does not restart playback.
      const next = stream ?? null;
      if (element.srcObject !== next) {
        element.srcObject = next;
      }

      // Route audio to a specific output device when one is requested and the
      // browser supports it. Failures (permission / unsupported) are non-fatal.
      const sinkable = element as SinkableMediaElement;
      if (sinkId && typeof sinkable.setSinkId === 'function') {
        sinkable.setSinkId(sinkId).catch(() => {
          /* output-device switching failed - keep the default device */
        });
      }
    },
    [stream, sinkId]
  );
}
